import type { ItemFieldRow } from './item-field-reader';
import { phpJsonInteger } from './php-integer-arithmetic';
import { phpJsonTruthy } from './php-json-values';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { encounterCreatureField } from '../../shared/array-types';
import { isSelfStatusOrigin } from '../statuses/status-origin';

function arrayLike(value: unknown): value is ItemFieldRow { return value !== null && typeof value === 'object'; }
function own(value: unknown, key: string): unknown {
    return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
}
/** PHP numeric buckets are lists only when their keys are contiguous. Children
 * are held read-only definitions: copying those graphs on a read loses identity. */
function statusBucket(entries: [string, unknown][]): ItemFieldRow {
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => child)
        : Object.fromEntries(entries);
}
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);

/** Only a missing caster key denotes an intern-elided self-cast. Explicit '' / null is environmental. */
export function readStatusCasterId(status: ItemFieldRow, targetFallback?: unknown): unknown {
    return Object.prototype.hasOwnProperty.call(status, 'status_caster_id') ? own(status, 'status_caster_id')
        : own(status, 'status_target_id') ?? targetFallback;
}

/** Stored entries for mutating consumers, including PHP sparse and singleton buckets. */
export function storedStatusRows(row: Record<string, unknown> | null, mode: 'target' | 'caster'): Record<string, unknown>[] {
    const stored = own(own(row, 'statuses'), mode);
    if (!arrayLike(stored)) return [];
    if (own(stored, 'status_id') !== undefined) return [stored as Record<string, unknown>];
    return Object.values(stored).filter((entry): entry is Record<string, unknown> => arrayLike(entry) && !Array.isArray(entry));
}

/** creature_statuses read-only copy. A supplied resolver owns its request memo; unavailable
 * results must throw. Omitting it models PHP's helper-not-loaded identity-shell fallback.
 */
export function readCreatureStatuses(row: ItemFieldRow | null, mode = 'target',
    resolveCarrierRef?: (id: string) => ItemFieldRow | null): ItemFieldRow {
    if (row !== null && !arrayLike(row)) throw new TypeError('Status reader requires a PHP array or null');
    const raw = own(own(row, 'statuses'), mode);
    const stored = arrayLike(raw) && own(raw, 'status_id') !== undefined ? [raw] : raw;
    if (!arrayLike(stored)) return [];
    const catalog = own(row, 'catalog');
    return statusBucket(Object.entries(stored).map(([key, entry]): [string, unknown] => {
        if (!arrayLike(entry)) return [key, entry];
        const copy = Object.fromEntries(Object.entries(entry));
        const caster = readStatusCasterId(entry);
        if (caster !== undefined) copy.status_caster_id = caster;
        if (arrayLike(copy.status_arr)) return [key, copy];
        const ref = copy.status_item_id ?? copy.status_arr;
        if (!validUuid(ref)) return [key, copy];
        const hit = own(catalog, ref) ?? own(catalog, ref.toUpperCase()) ?? own(catalog, ref.toLowerCase());
        let definition = arrayLike(hit) && own(own(hit, 'items'), 'item_id') != null
            && (own(hit, 'item_layer_types') != null || own(hit, 'computed') != null) ? hit : null;
        if (definition === null && resolveCarrierRef) definition = resolveCarrierRef(ref);
        copy.status_arr = definition ?? { items: { item_id: ref } };
        return [key, copy];
    }));
}

type LayerValueReader = (row: ItemFieldRow | null, key: string) => unknown;
const scalarId = (value: unknown): string => {
    const id = arrayLike(value) ? own(value, '0') ?? '' : value;
    return typeof id === 'string' ? id.trim() : '';
};

/** Physical peers shared by expiry planning and native connection teardown. */
export function statusConnectionPeers<T extends Record<string, unknown>>(status: T, rows: readonly T[]): T[] {
    const connection = scalarId(own(status, 'status_connected_id')).toUpperCase();
    return rows.filter(row => scalarId(own(row, 'status_connected_id')).toUpperCase() === connection);
}

/** Browser-only hostile/summons self-anchor classification.
 * The anchor an opposing-team-only (or summons-only) follow aura stamps on its own caster is the aura's SOURCE: payload
 * consumers skip it, structural readers keep it. A null/'' caster id is NOT self.
 */
export function isHostileAuraSelfAnchor(definition: unknown, aoeSource: unknown, casterId: unknown, targetId: unknown,
    layerValue: LayerValueReader): boolean {
    const source = arrayLike(aoeSource) ? own(aoeSource, '0') ?? 0 : aoeSource ?? 0;
    if (Number(phpJsonInteger(source)) !== 1 || !arrayLike(definition)) return false;
    // NULL / '' caster (environmental) is not self; an elided self-cast caster is re-stamped by readCreatureStatuses.
    const target = scalarId(targetId), caster = scalarId(casterId);
    if (target === '' || caster.toLowerCase() !== target.toLowerCase()) return false;
    for (const layer of ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target']) {
        if (layerValue(definition, layer) == null) return false;
    }
    // a summons-only aura never reaches its owner either — the owner is not its own summon (2026-10-07)
    if (phpJsonTruthy(layerValue(definition, 'effect_only_affects_your_summons'))) return true;
    return phpJsonTruthy(layerValue(definition, 'effect_only_affects_opposing_team_type'))
        && !phpJsonTruthy(layerValue(definition, 'effect_only_affects_same_team_type'));
}

/** The sole aura payload eligibility reader. Raw/structural readers keep every anchor. */
export function readCreaturePayloadStatuses(row: ItemFieldRow | null, layerValue: LayerValueReader,
    resolveCarrierRef?: (id: string) => ItemFieldRow | null,
    resolveOwner?: AuraOwnerStateResolver): ItemFieldRow {
    const rows = readCreatureStatuses(row, 'target', resolveCarrierRef);
    resolveOwner ??= compilingOwner;
    // Several anchors may name one owner. Resolve that held row once for this read;
    // a later resource/status delta starts a fresh read of the canonical row.
    const owners = new Map<string, ItemFieldRow | null>();
    const heldOwner: AuraOwnerStateResolver | undefined = resolveOwner && (id => {
        const key = id.toUpperCase();
        if (!owners.has(key)) owners.set(key, resolveOwner(id));
        return owners.get(key)!;
    });
    return statusBucket(Object.entries(rows).filter(([, entry]) => !arrayLike(entry)
        || (!isHostileAuraSelfAnchor(own(entry, 'status_arr'), own(entry, 'status_aoe_source'),
            own(entry, 'status_caster_id'), own(entry, 'status_target_id'), layerValue)
            && !isDormantOwnerGatedAura(entry, layerValue, heldOwner, row))));
}

/** R-116 part 2 (2026-09-30, Compiler IV): the layers an owner-gated aura may carry whose read on the HOLDER never depends on the aura's
 * live state. Structural / gate / requirement layers are not DynamicLayer entries, so layer_on_status_effects never merges them onto the
 * holder in either state; the per-skill skill_roll_* lists are excluded from its catch-all — their compiled lines (computed.creature.skills)
 * are the one state-dependent output, and the C1 compile re-derives them from the owner's CURRENT state on every claim
 * (possessor-compile.ts, "aura-owner re-derivation"). Anything else an owner-gated aura carries is live payload. */
export const AURA_OWNER_STATE_FREE_LAYERS: ReadonlySet<string> = new Set(['if_aura_owner_has_status_effect_active', 'aoe_type', 'aoe_distance',
    'aoe_distance_height', 'aoe_distance_depth', 'aoe_follows_target', 'persistent_aoe', 'range', 'effect_only_affects_same_team_type',
    'effect_only_affects_opposing_team_type', 'effect_only_affects_your_summons', 'duration_int', 'duration_type', 'requires_class', 'requires_class_level', 'requires_subclass',
    'requires_level', 'unenchantable', 'triggers_after_combat_begins', 'cost_gp', 'skill_roll_adv', 'skill_roll_dis', 'skill_roll_mod']);
/** The status GRANT layers: an ACTIVE status appends their abilities (and its origin's — ft_merge_status_origin_ability_layers) to the
 * holder's buckets, whose own layers then compile into any cluster (layer_on_status_effects). */
export const STATUS_GRANT_LAYERS: readonly string[] = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability', 'free_ability',
    'reaction_ability', 'opportunity_attack_ability', 'curse', 'sanguine', 'menta', 'innate_spell_per_long_rest', 'at_will_spell'];
const carriesLayer = (def: unknown, name: string): boolean => own(own(def, 'item_layer_types'), name) != null || own(def, name) != null;
/** Whether a held owner-gated aura row makes the holder's CACHED value of `name` depend on the owner's live state (the readers' guard —
 * the owner's 2026-09-29 rule: a cached blob cannot model a live owner flip). A foreign origin or any grant layer can compile anything;
 * otherwise only a carried layer outside AURA_OWNER_STATE_FREE_LAYERS. Not an owner-gated aura → false. */
export function ownerGatedAuraLivePayload(status: unknown, name: string): boolean {
    const def = own(status, 'status_arr');
    if (!arrayLike(def) || !carriesLayer(def, 'if_aura_owner_has_status_effect_active')) return false;
    const origin = scalarId(own(status, 'status_origin_id'));
    const item = scalarId(own(status, 'status_item_id') ?? own(own(def, 'items'), 'item_id'));
    if (origin !== '' && origin.toUpperCase() !== item.toUpperCase()
        && !isSelfStatusOrigin(scalarId(own(status, 'status_id')), origin)) return true;
    if (STATUS_GRANT_LAYERS.some(layer => carriesLayer(def, layer))) return true;
    return carriesLayer(def, name) && !AURA_OWNER_STATE_FREE_LAYERS.has(name);
}

export type AuraOwnerStateResolver = (id: string) => ItemFieldRow | null;
let compilingOwner: AuraOwnerStateResolver | undefined;
/** Synchronous member compile scope. Workers and offline callers supply the same
 * held rows; a payload read never fetches or recompiles an owner. */
export function withAuraOwnerState<T>(resolve: AuraOwnerStateResolver | undefined, run: () => T): T {
    const previous = compilingOwner;
    compilingOwner = resolve ?? previous;
    try { return run(); } finally { compilingOwner = previous; }
}
export class AuraOwnerGateUnavailable extends Error {
    readonly reason: string;
    constructor(reason = 'aura-owner-state-not-held') {
        super('unsupported-creature_payload_statuses:' + reason);
        this.reason = 'creature_payload_statuses:' + reason;
    }
}
const vitalityIds = ['82788F70-EBEF-44C4-80E7-6622AD20DD60', 'FF30B76E-785D-2CAF-FC66-C131D0548808',
    '658483AA-34DE-74E5-C974-E3E202C31CC7', 'C97076EF-FED9-19B4-D109-C22F9BC740E8',
    '44B8792E-632E-2651-C666-67DFD181E92A', '298BE1DD-2917-476E-D79C-92CF8C598926',
    'D5DE14C4-3194-87BC-D767-6D45BDD90552', '8AE129AF-52D8-CCE8-706A-85767DB52740'];

/** Owner 2026-09-29, Priest Martyr Eternal. Unknown owner truth refuses the client lane. */
export function isDormantOwnerGatedAura(status: ItemFieldRow, layerValue: LayerValueReader,
    resolveOwner?: AuraOwnerStateResolver, carrier: ItemFieldRow | null = null): boolean {
    const def = own(status, 'status_arr');
    if (!arrayLike(def)) return false;
    if (own(own(def, 'item_layer_types'), 'if_aura_owner_has_status_effect_active') == null
        && own(def, 'if_aura_owner_has_status_effect_active') == null) return false;
    const id = scalarId(own(status, 'status_caster_id'));
    if (!validUuid(id)) return true;
    const roster = own(own(carrier, 'encounter_arr'), 'encounter_creatures_arr');
    const matchesOwner = (candidate: unknown): boolean =>
        scalarId(own(own(candidate, 'encounter_creatures'), 'encounter_creature_id')).toUpperCase() === id.toUpperCase();
    const resolver = resolveOwner ?? compilingOwner;
    const owner = matchesOwner(carrier) && own(own(own(carrier, 'computed'), 'vitality'), 'v_max') != null ? carrier
        : resolver ? resolver(id) : matchesOwner(carrier) ? carrier
        : arrayLike(roster) ? Object.values(roster).find(matchesOwner) : undefined;
    if (!arrayLike(owner)) {
        if (resolver) return true; // explicit resolver can prove that the owner is absent
        throw new AuraOwnerGateUnavailable();
    }
    const ec = own(owner, 'encounter_creatures');
    const field = (key: string): unknown => encounterCreatureField(ec, key);
    if (Number(field('encounter_creature_dead') ?? 0) !== 0 || Number(field('encounter_creature_deleted') ?? 0) !== 0) return true;
    // Scalar readers unwrap [0]; the OR list must come from the complete authored slot.
    const slot = own(own(def, 'item_layer_types'), 'if_aura_owner_has_status_effect_active')
        ?? own(def, 'if_aura_owner_has_status_effect_active');
    const entries = arrayLike(slot) && Object.prototype.hasOwnProperty.call(slot, '0') ? Object.values(slot) : [slot];
    const needed = entries.map(entry => (readLayerListEntryUuid(entry) ?? '').toUpperCase());
    const ordinary = needed.filter(value => value !== '' && !vitalityIds.includes(value));
    if (ordinary.length) {
        const regular = Object.values(readCreatureStatuses(owner, 'target')).filter(arrayLike)
            .filter(row => Number(own(row, 'status_deleted') ?? 0) === 0)
            .map(row => scalarId(own(row, 'status_item_id') ?? own(own(own(row, 'status_arr'), 'items'), 'item_id')).toUpperCase());
        if (ordinary.some(value => regular.includes(value))) return false;
    }
    // Ordinary-status gates need no vitality data. Lean rosters omit an empty target bucket.
    if (!needed.some(value => vitalityIds.includes(value))) return true;
    const numeric = (value: unknown): number | null => {
        const scalar = arrayLike(value) ? own(value, '0') : value;
        return (typeof scalar === 'number' || (typeof scalar === 'string' && scalar.trim() !== ''))
            && Number.isFinite(Number(scalar)) ? Number(scalar) : null;
    };
    const current = numeric(field('v_current') ?? 0);
    const max = numeric(own(own(own(owner, 'computed'), 'vitality'), 'v_max'));
    const temp = numeric(field('v_temp') ?? 0);
    if (current === null || max === null || temp === null) throw new AuraOwnerGateUnavailable();
    const ratio = max > 0 ? current / max : 0;
    const vId = vitalityIds[max <= 0 ? 7 : ratio >= 1 ? (temp > 0 ? 0 : 1)
        : ratio > .75 ? 2 : ratio > .5 ? 3 : ratio > .25 ? 4 : ratio > .1 ? 5 : ratio > 0 ? 6 : 7];
    return !needed.includes(vId!);
}
